import type { Blog, BlogImage } from '@/app/types/blog'
import { toImageSrc } from '@/app/utils/image'

/** Picks a cover size (falls back to the full cover) and turns it into a full URL. */
export function getCoverUrl(blog: Blog, key: BlogImage['key'] = 'cover'): string | null {
  const covers = blog?.cover ?? []
  const image = covers.find(c => c.key === key) ?? covers.find(c => c.key === 'cover') ?? covers[0]
  return image?.path ? toImageSrc(image.path) : null
}

export function getCategoryName(cat: Record<string, any>, language: string): string {
  return cat?.[`name_${language}`] || cat?.name || ''
}

export function formatBlogDate(date: string, locale: string): string {
  const d = new Date(date)
  if (!date || Number.isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat(locale === 'sq' ? 'sq-AL' : 'en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d)
}

export function getReadingMinutes(html: string): number {
  const text = (html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&[a-z#0-9]+;/gi, ' ')
  const words = text.split(/\s+/).filter(Boolean).length
  return Math.max(1, Math.round(words / 200))
}

/**
 * Cleans the CMS HTML before rendering:
 *  - &nbsp; between every word (from the editor) -> normal spaces, so text wraps
 *  - removes empty spacer paragraphs
 *  - drops the first <h1> (the page already shows the title), other h1s become h2s
 *  - strips scripts, inline event handlers and javascript: links
 */
export function prepareBlogHtml(html: string): string {
  let out = html ?? ''

  out = out
    .replace(/<(script|style|object|embed)[\s\S]*?<\/\1>/gi, '')
    .replace(/<(script|style|object|embed)[^>]*\/?>/gi, '')
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/(href|src)\s*=\s*(["'])\s*javascript:[^"']*\2/gi, '$1="#"')

  out = out.replace(/&nbsp;/g, ' ').replace(/ /g, ' ')
  out = out.replace(/<p>(\s|<br\s*\/?>)*<\/p>/gi, '')
  out = out.replace(/^\s*<h1[^>]*>[\s\S]*?<\/h1>/i, '')
  out = out.replace(/<h1(\s[^>]*)?>/gi, '<h2$1>').replace(/<\/h1>/gi, '</h2>')

  return out.trim()
}
